import type { RcScopeType } from '../shipments/shipments-types.js';

export const regulatoryIdentityKindValues = ['RC', 'PR'] as const;
export type RegulatoryIdentityKind = (typeof regulatoryIdentityKindValues)[number];

export interface RegulatoryIdentity {
  kind: RegulatoryIdentityKind;
  number: string;
  normalizedNumber: string;
  displayNumber: string;
}

export const companyReceiverTypeValues = ['PROCESSOR', 'RECYCLER', 'OTHER'] as const;
export type CompanyReceiverType = (typeof companyReceiverTypeValues)[number];

export interface PartyAddressSnapshot {
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  state: string;
  postalCode: string;
}

export const counterpartyRefKindValues = [
  'INTERNAL_RC_SCOPE',
  'COMPANY_RECEIVER',
  'REGULATORY_ENTITY',
] as const;
export type CounterpartyRefKind = (typeof counterpartyRefKindValues)[number];

export interface InternalRcScopeCounterpartyRef {
  kind: 'INTERNAL_RC_SCOPE';
  companyId: string;
  rcScopeType: RcScopeType;
  rcScopeId: string;
  regulatoryEntityId?: string | null;
}

export interface CompanyReceiverCounterpartyRef {
  kind: 'COMPANY_RECEIVER';
  companyReceiverId: string;
  regulatoryEntityId?: string | null;
}

export interface RegulatoryEntityCounterpartyRef {
  kind: 'REGULATORY_ENTITY';
  regulatoryEntityId: string;
}

export type CounterpartyRef =
  | InternalRcScopeCounterpartyRef
  | CompanyReceiverCounterpartyRef
  | RegulatoryEntityCounterpartyRef;

export interface InternalRcScopeCounterpartySnapshot {
  kind: 'INTERNAL_RC_SCOPE';
  counterpartyRef: InternalRcScopeCounterpartyRef;
  companyId: string;
  rcScopeType: RcScopeType;
  rcScopeId: string;
  regulatoryIdentity: RegulatoryIdentity;
  regulatoryEntityId?: string | null;
  displayName: string;
  companyName: string | null;
  storeName: string | null;
  locationName: string | null;
  address?: PartyAddressSnapshot | null;
}

export interface CompanyReceiverCounterpartySnapshot {
  kind: 'COMPANY_RECEIVER';
  counterpartyRef: CompanyReceiverCounterpartyRef;
  companyReceiverId: string;
  regulatoryIdentity: RegulatoryIdentity;
  regulatoryEntityId?: string | null;
  displayName: string;
  receiverType: CompanyReceiverType;
  address?: PartyAddressSnapshot | null;
}

export interface RegulatoryEntityCounterpartySnapshot {
  kind: 'REGULATORY_ENTITY';
  counterpartyRef: RegulatoryEntityCounterpartyRef;
  regulatoryEntityId: string;
  regulatoryIdentity: RegulatoryIdentity;
  displayName: string;
  receiverType?: CompanyReceiverType | null;
  address?: PartyAddressSnapshot | null;
}

export type CounterpartySnapshot =
  | InternalRcScopeCounterpartySnapshot
  | CompanyReceiverCounterpartySnapshot
  | RegulatoryEntityCounterpartySnapshot;

export interface InternalRcScopeCounterpartySelection {
  kind: 'INTERNAL_RC_SCOPE';
  counterpartyRef: InternalRcScopeCounterpartyRef;
  companyId: string;
  rcScopeType: RcScopeType;
  rcScopeId: string;
  regulatoryIdentity: RegulatoryIdentity;
  regulatoryEntityId?: string | null;
  displayName: string;
  companyName?: string | null;
  storeName?: string | null;
  locationName?: string | null;
  address?: PartyAddressSnapshot | null;
}

export interface CompanyReceiverCounterpartySelection {
  kind: 'COMPANY_RECEIVER';
  counterpartyRef: CompanyReceiverCounterpartyRef;
  companyReceiverId: string;
  regulatoryIdentity: RegulatoryIdentity;
  regulatoryEntityId?: string | null;
  displayName: string;
  receiverType: CompanyReceiverType;
  address?: PartyAddressSnapshot | null;
}

export interface RegulatoryEntityCounterpartySelection {
  kind: 'REGULATORY_ENTITY';
  counterpartyRef: RegulatoryEntityCounterpartyRef;
  regulatoryEntityId: string;
  regulatoryIdentity: RegulatoryIdentity;
  displayName: string;
  receiverType?: CompanyReceiverType | null;
  address?: PartyAddressSnapshot | null;
}

export type CounterpartySelection =
  | InternalRcScopeCounterpartySelection
  | CompanyReceiverCounterpartySelection
  | RegulatoryEntityCounterpartySelection;

export const counterpartySearchSourceValues = ['internal', 'company', 'global'] as const;
export type CounterpartySearchSource = (typeof counterpartySearchSourceValues)[number];

export const regulatoryEntityVerificationStatusValues = [
  'UNVERIFIED',
  'VERIFIED',
  'REJECTED',
  'MERGED',
  'INACTIVE',
] as const;
export type RegulatoryEntityVerificationStatus =
  (typeof regulatoryEntityVerificationStatusValues)[number];

export interface RegulatoryEntityRecord {
  id: string;
  identity: RegulatoryIdentity;
  displayName: string;
  receiverType?: CompanyReceiverType | null;
  verificationStatus: RegulatoryEntityVerificationStatus;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  createdAt: string;
  updatedAt: string;
}
